import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { RouterLink } from '@angular/router';

import { ReadonlyDisplayBase } from './readonly-display.base';
import type { TitlePart } from '../../../../utils/title-parts.types';
import { SUBTITLE_SEPARATOR } from '../../../../utils/title-parts.util';
import { TitlePartsComponent } from '../../../title-parts.component';
import { displayContextFor, entryRouteResolverOf, entrySummaryResolverOf } from '../display-context.util';
import { READONLY_LINK_STYLES } from './link-styles';
import { LinkedEntryDisplayComponent, isLinkedEntryEnvelope, linkedEntryLabel } from './linked-entry-display.component';

/** One linked entry as it is drawn: its title (a link where it can be placed) and detail line. */
interface SummaryItem {
  titleParts: TitlePart[];
  details: string[];
  route: string[] | null;
}

/**
 * Read display that shows a linked entry as a short summary instead of only its name (#21447):
 * a vehicle's Overzicht shows its company as "Kuipers Koeltransport" over
 * "Koelhuisstraat 3, 8321 WD Urk · +31 527 681 200".
 *
 * Opt-in per field through `props.readonlyDisplay: 'linked-entry-summary'` (form builder:
 * "Leesweergave"). The lines are the linked module's own header (`headerTitleKeyPaths` /
 * `headerSubtitleKeyPaths`, built by `buildLinkedEntrySummary`), so there is nothing to configure
 * on the linking field itself.
 *
 * Reads the envelope stored in the answer, no extra request. Presentational like the plain linked
 * display: the summary and the route come from `formState` resolvers. Without a summary resolver
 * (Storybook, a standalone form) or without anything to summarise (a typed-in string, an empty
 * answer), it renders exactly the plain linked display, so switching this on never makes a row
 * read worse than before.
 */
@Component({
  selector: 'dd-linked-entry-summary-display',
  imports: [RouterLink, TitlePartsComponent, LinkedEntryDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    READONLY_LINK_STYLES,
    `
      :host {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        min-width: 0;
      }
    `,
  ],
  // The separator (SUBTITLE_SEPARATOR, the same one the detail header's subtitle uses) is a real
  // text node for the same reason as the plain display's ', ': the row is copied and read by
  // assistive tech, and CSS generated content is in neither.
  template: `
    @for (item of items(); track $index) {
      <div class="flex min-w-0 flex-col gap-0.5" data-cy="linked-entry-summary">
        @if (item.titleParts.length) {
          <span class="flex min-w-0 font-medium" data-cy="linked-entry-summary-title">
            @if (item.route; as route) {
              <a class="min-w-0" [routerLink]="route"><dd-title-parts [parts]="item.titleParts" /></a>
            } @else {
              <dd-title-parts [parts]="item.titleParts" />
            }
          </span>
        }
        @if (item.details.length) {
          <span class="text-secondary text-sm" data-cy="linked-entry-summary-details">
            @for (detail of item.details; track $index) {
              <span>{{ detail }}</span>
              @if (!$last) {
                <span>{{ separator }}</span>
              }
            }
          </span>
        }
      </div>
    } @empty {
      <dd-linked-entry-display [field]="field()" [value]="value()" />
    }
  `,
})
export class LinkedEntrySummaryDisplayComponent extends ReadonlyDisplayBase {
  protected readonly separator = SUBTITLE_SEPARATOR;

  readonly items = computed<SummaryItem[]>(() => {
    const field = this.field();
    const summarize = entrySummaryResolverOf(field);
    if (!summarize) {
      return [];
    }
    const resolveRoute = entryRouteResolverOf(field);
    const context = displayContextFor(field);
    const raw = this.value();

    return (Array.isArray(raw) ? raw : [raw])
      .filter(isLinkedEntryEnvelope)
      .map(envelope => {
        const summary = summarize(envelope);
        // No composed title on the linked module: name the entry the way the plain display does
        // (card title, then the stored selectedInput), so the summary never loses the name.
        const label = linkedEntryLabel(envelope, field, context);
        const titleParts: TitlePart[] = summary?.titleParts.length
          ? summary.titleParts
          : label
            ? [{ kind: 'text', value: label }]
            : [];
        const route = resolveRoute?.(envelope);
        return { titleParts, details: summary?.details ?? [], route: route?.length ? route : null };
      })
      .filter(item => item.titleParts.length > 0 || item.details.length > 0);
  });
}
